import uniqBy from 'lodash/uniqBy.js';
import { Utils } from '@sbs/uikit-constructor';
import {
    BASE_SIDEBAR_ITEMS,
    IMAGE_LIB_FILE_TYPE,
    IMAGE_LIB_MAX_FILE_SIZE,
    ImageLibSortTypes,
} from '@/constants/imageLibrary.js';
import { getUploadProgress } from '@/utils/utils.js';
import { fileLibraryApi } from '@/api/fileLibrary.js';
import { validateFile } from '@/utils/validators.js';
import useSnackbar from '@/hooks/snackbars.js';
import { getErrorMsg } from '@/utils/helpers.js';
import { addActivityAttachment } from '@/api/attachmentsApi.js';
import { getImageSize } from '@/utils/fileData.js';
import { FILE_LIB_MAX_FILE_UPLOAD } from '@/admin/components/ImageLibrary/ImageLibraryModal/constants.js';
import { createCancelHttp, isHttpCancel } from '@/utils/http.js';

const { createSnackbar } = useSnackbar();

export default {
    namespaced: true,
    state: {
        activityId: null,
        acceptCallback: null,
        closeCallback: null,
        createAttachmentOnSelect: true,
        selecting: false,
        currentPreviewFileIndex: 0,
        previewFiles: [],
        showAi: false,
        search: '',
        sort: ImageLibSortTypes.NEWEST,
        breadcrumb: [],
        activeItem: null,
        selectedItems: [],
        parentItem: null,
        editItem: null,
        uploadingItems: [],
        copyItems: [],
        copyDest: null,
        moveItems: [],
        moveDest: null,
        cropItem: null,
        openedSidebarItem: null,
    },
    getters: {
        previewFiles: state => state.previewFiles,
        previewFilesActiveIndex: state => state.currentPreviewFileIndex,
        currentPreviewFile: state => state.previewFiles[state.currentPreviewFileIndex] ?? null,
        currentUploadingItems: state => parentId => (parentId ? state.uploadingItems.filter(item => item.parentId === parentId) : []),
        isShowModalLib: state => Boolean(state.activityId) || !state.createAttachmentOnSelect,
    },
    mutations: {
        setSearch(state, search) { state.search = search; },
        setActiveItem(state, item) { state.activeItem = item; },
        setSort(state, sort) { state.sort = sort; },
        selectItems(state, items) { state.selectedItems = uniqBy(items, 'id'); },
        clearSelectedItems(state) { state.selectedItems = []; },
        CLEAR_PREVIEW_FILES(state) { state.previewFiles = []; },
        SET_CURRENT_PREVIEW_FILE_INDEX(state, index) { state.currentPreviewFileIndex = index; },
        selecting(state, selecting) { state.selecting = selecting; },
        setFavoriteStateForItems(state, { inFavorites, itemIds }) {
            const getUpdateItemFavoriteState = item => {
                if (item && itemIds.includes(item.id)) {
                    return {
                        ...item,
                        in_favorites: inFavorites,
                    };
                }

                return item;
            };

            state.activeItem = getUpdateItemFavoriteState(state.activeItem);
            state.previewFiles = state.previewFiles.map(getUpdateItemFavoriteState);
        },
        setShowAi(state, show) {
            state.showAi = show;
        },
        /**
         * @param state
         * @param {{ items: Array<*>, activeIndex: number }} payload
         */
        showPreview(state, payload) {
            state.currentPreviewFileIndex = payload.activeIndex;
            state.previewFiles = payload.items;
        },
        showCreateItem(state, parentItem) {
            state.parentItem = parentItem;
            state.editItem = null;
        },
        showEditItem(state, editItem) {
            state.editItem = editItem;
            state.parentItem = null;
        },
        hideEditOrCreateItem(state) {
            state.editItem = null;
            state.parentItem = null;
        },
        updateItemName(state, { itemId, newName }) {
            if (state.activeItem?.id === itemId) {
                state.activeItem.name = newName;
            }

            if (state.editItem?.id === itemId) {
                state.editItem.name = newName;
            }

            state.selectedItems = state.selectedItems.map(item => (item.id === itemId
                ? ({
                    ...item,
                    name: newName,
                })
                : item));
        },

        addUploadItem(state, uploadItem) {
            state.uploadingItems.push(uploadItem);
        },

        updateUploadItemProgress(state, { itemId, progress }) {
            const itemIndex = state.uploadingItems.findIndex(item => item.id === itemId);

            if (itemIndex > -1) {
                state.uploadingItems[itemIndex].progress = progress;
            }
        },

        removeUploadItemProgress(state, itemId) {
            const removeIndex = state.uploadingItems.findIndex(item => item.id === itemId);

            if (removeIndex > -1) {
                const removedItem = state.uploadingItems[removeIndex];

                state.uploadingItems = state.uploadingItems.toSpliced(removeIndex, 1);

                URL.revokeObjectURL(removedItem.preview);
            }
        },
        setCopyItems(state, items) {
            state.copyItems = items;
            state.copyDest = null;
        },
        setCopyDest(state, copyDest) {
            state.copyDest = copyDest;
        },
        clearCopyItems(state) {
            state.copyItems = [];
        },
        setMoveItems(state, items) {
            state.moveItems = items;
            state.moveDest = null;
        },
        setMoveDest(state, moveDest) {
            state.moveDest = moveDest;
        },
        clearMoveItems(state) {
            state.moveItems = [];
        },

        setCropItem(state, item) {
            state.cropItem = item;
            state.previewFiles = [];
        },

        showFileLib(state, { activityId, onAccept, onClose, createAttachmentOnSelect = true }) {
            state.search = '';
            state.sort = ImageLibSortTypes.NEWEST;
            state.activityId = activityId;
            state.acceptCallback = onAccept;
            state.closeCallback = onClose;
            state.createAttachmentOnSelect = createAttachmentOnSelect;
        },

        hideFileLib(state) {
            state.closeCallback?.();

            state.activityId = null;
            state.acceptCallback = null;
            state.closeCallback = null;
            state.createAttachmentOnSelect = true;
        },

        setOpenedSidebarItem(state, data) {
            state.openedSidebarItem = data;
        },

        clearOpenedSidebarItem(state) {
            state.openedSidebarItem = null;
        },
    },
    actions: {
        async openFileLib({ dispatch, commit }, { courseId, projectId, activityId, onAccept, onClose, createAttachmentOnSelect = true }) {
            try {
                await dispatch('activateCourseFolder', {
                    courseId,
                    projectId,
                });
                commit('showFileLib', {
                    activityId,
                    onAccept,
                    onClose,
                    createAttachmentOnSelect,
                });
            } catch (e) {
                console.error(e);
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка открытия библиотеки файлов',
                });
            }
        },
        clearPreviewFiles({ commit }) {
            commit('CLEAR_PREVIEW_FILES');
        },
        setCurrentPreviewFileIndex({ commit }, index) {
            commit('SET_CURRENT_PREVIEW_FILE_INDEX', index);
        },
        async renameItem({ commit }, { itemId, newName }) {
            await fileLibraryApi.renameItem(itemId, newName);
            commit('updateItemName', {
                itemId,
                newName,
            });
        },

        createUploadItem({ commit }, payload) {
            const { parentId, file } = payload;
            const uploadItemId = Utils.Helpers.getRandomId();
            const canceling = createCancelHttp(uploadItemId);

            const preview = URL.createObjectURL(file);

            commit('addUploadItem', {
                id: uploadItemId,
                file,
                parentId,
                progress: 0,
                preview,
                canceling,
            });

            return {
                id: uploadItemId,
                canceling,
            };
        },

        /**
         * @param ctx
         * @param {{ parentId: string|number, type: 'file'|'folder', name?: string, file: File }} payload
         * @returns {Promise<void>}
         */
        async createItem({ dispatch, commit }, payload) {
            const { parentId, type, name, file } = payload;

            let uploadItem = null;

            try {
                if (file) {
                    await validateFile(file, {
                        types: IMAGE_LIB_FILE_TYPE,
                        maxSize: IMAGE_LIB_MAX_FILE_SIZE,
                        messages: {
                            types: 'Неверный формат файла для загрузки',
                            maxSize: 'Размер одного файла не должен превышать допустимый лимит в 10 МБ',
                        },
                    });

                    uploadItem = await dispatch('createUploadItem', {
                        parentId,
                        file,
                    });
                }

                await fileLibraryApi.createItem({
                    type,
                    parent_id: parentId,
                    name: name || file.name,
                    file,
                }, {
                    cancelToken: uploadItem?.canceling?.token,
                    onUploadProgress: event => {
                        if (uploadItem?.id) {
                            commit('updateUploadItemProgress', {
                                itemId: uploadItem.id,
                                progress: getUploadProgress(event),
                            });
                        }
                    },
                });
            } finally {
                if (uploadItem) {
                    commit('removeUploadItemProgress', uploadItem.id);
                }
            }
        },

        /**
         * @param ctx
         * @param {{ parentId: string|number, name?: string, file: File }} payload
         * @returns {Promise<void>}
         */
        async createItemFile({ dispatch }, payload) {
            await dispatch('createItem', {
                ...payload,
                type: 'file',
            });
        },

        cancelUploading({ state }) {
            state.uploadingItems.forEach(uploadItem => {
                uploadItem.canceling.cancel();
            });
        },

        /**
         * @param ctx
         * @param {{ parentId: string|number, files: File[] }} payload
         * @returns {Promise<void>}
         */
        async createItemFiles({ dispatch }, payload) {
            const { files, ...restPayload } = payload;

            let fileList = Array.from(files ?? []);

            if (fileList.length > FILE_LIB_MAX_FILE_UPLOAD) {
                createSnackbar({
                    type: 'error',
                    message: `Одновременно можно выбрать ${FILE_LIB_MAX_FILE_UPLOAD} файлов`,
                });
                fileList = fileList.slice(0, FILE_LIB_MAX_FILE_UPLOAD);
            }

            const uploadingFiles = fileList.map(file => dispatch('createItemFile', {
                ...restPayload,
                file,
            }));

            const results = await Promise.allSettled(uploadingFiles);

            const errors = results.filter(result => result.status === 'rejected' && !isHttpCancel(result.reason));

            if (errors.length) {
                createSnackbar({
                    type: 'error',
                    message: errors.length === 1 ? getErrorMsg(errors[0].reason) : 'Ошибка. Попробуйте еще раз',
                });
                console.error(errors.map(err => err.reason));
                throw errors[0].reason;
            }
        },

        /**
         * @param ctx
         * @param {{ parentId: string|number, name: string }} payload
         * @returns {Promise<void>}
         */
        async createItemFolder({ dispatch }, payload) {
            await dispatch('createItem', {
                ...payload,
                type: 'folder',
            });
        },

        activateCourseFolder: async ({ commit }, { courseId, projectId }) => {
            let fileLibCourseItem = null;

            try {
                if (courseId) {
                    fileLibCourseItem = await fileLibraryApi.getLibIdForCourse(courseId);
                } else if (projectId) {
                    fileLibCourseItem = await fileLibraryApi.getLibIdForProject(projectId);
                }
            } catch (e) {
                console.error(e);
                createSnackbar({
                    type: 'error',
                    message: 'Не удалось открыть папку курса',
                    timeout: 1500,
                });
            }

            commit('clearOpenedSidebarItem');

            if (fileLibCourseItem) {
                commit('setActiveItem', fileLibCourseItem);
            } else {
                commit('setActiveItem', BASE_SIDEBAR_ITEMS[0]);
            }
        },

        /**
         * @param ctx
         * @param {{ courseId: number | string, file: File }} payload
         * @returns {Promise<void>}
         */
        async createImageFileInCourse({ dispatch }, payload) {
            try {
                const { courseId, file } = payload;
                const fileLibCourseItem = await fileLibraryApi.getLibIdForCourse(courseId);

                if (!fileLibCourseItem) {
                    throw new Error('Элемент библиотеки не найден');
                }

                await dispatch('createItemFile', {
                    parentId: fileLibCourseItem.id,
                    file,
                });

                createSnackbar({
                    type: 'success',
                    size: 'large',
                    title: 'Изображение добавлено',
                    message: 'Изображение добавлено в библиотеку картинок в папку текущего потока',
                    maxWidth: 450,
                });
            } catch (e) {
                if (isHttpCancel(e)) return;

                console.error(e);
                createSnackbar({
                    type: 'error',
                    message: 'Ошибка. Попробуйте еще раз',
                });
            }
        },

        setFavoriteStateItems({ commit }, itemIds) {
            commit('setFavoriteStateForItems', {
                itemIds,
                inFavorites: true,
            });
        },
        setUnFavoriteStateItems({ commit }, itemIds) {
            commit('setFavoriteStateForItems', {
                itemIds,
                inFavorites: false,
            });
        },

        // eslint-disable-next-line max-lines-per-function,max-statements
        async selectFileLibItem({ commit, dispatch, state }, payload) {
            try {
                commit('selecting', true);

                const {
                    activityId,
                    acceptCallback,
                    createAttachmentOnSelect,
                } = state;

                const { item } = payload;

                if (!acceptCallback) return;

                if (!createAttachmentOnSelect) {
                    acceptCallback(item);
                    commit('hideFileLib');

                    return;
                }

                if (!activityId) return;

                const { width, height } = await getImageSize(fileLibraryApi.itemServe(item.id));

                const { data: respData } = await addActivityAttachment(activityId, {
                    name: item.name,
                    file_library_item_id: item.id,
                });

                const attach = {
                    ...respData.data,
                    width,
                    height,
                };

                await dispatch('longread/saveNewAttackToBlock', {
                    blockId: activityId,
                    attach,
                }, { root: true });

                acceptCallback(attach);

                commit('hideFileLib');
            } catch (e) {
                console.error({ e });
                createSnackbar({
                    type: 'error',
                    message: getErrorMsg(e),
                });
            } finally {
                commit('selecting', false);
            }
        },
        updateFavoritesStates({ commit }, { inFavorites, itemIds }) {
            commit('setPreviewStateFavoriteItems', {
                inFavorites,
                itemIds,
            });
        },
    },
};
